import { useTranslation } from 'react-i18next';
import { usePromoContext } from '../../../hooks/usePromoContext';
import { SectionHeading, Reveal } from './_shared';
import { JourneyCards, ListingRow, PhoneTop, type JourneyCard } from './JourneyCards';
import dammamImg from '../../../assets/cities/dammam.webp';
import makkahImg from '../../../assets/cities/makkah.webp';

// The home page's closing row (owner, 2026-10-07: "change them to something
// professional"). It was a quiet strip of three text columns: create an
// account, list your property (already the first action card higher up) and a
// newsletter that only said «قريباً». It is now three journey cards in the same
// frame as the action cards (JourneyCards.tsx), each pointing somewhere real:
//
// - the account: a guest is invited to create one; a member goes to their
//   favourites, where saved homes and the advertisers' replies live;
// - the professionals directory (brokers, appraisers, property managers);
// - the advertisers' plans.

// Mockup UI copy. Illustrative screens, not data: no price in a mockup is
// presented as a market fact, and no person is named.
const MOCK = {
  ar: {
    saved: 'المفضلة',
    reply: 'ردّ المعلن على استفسارك',
    row1: 'فيلا · الدمام',
    row2: 'شقة · مكة المكرمة',
    perYear: '/سنة',
    broker: 'وسيط عقاري مرخّص',
    appraiser: 'مقيّم عقاري معتمد',
    manager: 'إدارة أملاك',
    riyadh: 'الرياض',
    jeddah: 'جدة',
    dammam: 'الدمام',
    monthly: 'شهري',
    yearly: 'سنوي',
    plan: 'الباقة الاحترافية',
    feature1: 'إعلانات أكثر',
    feature2: 'تمييز الإعلانات',
    feature3: 'إحصائيات المشاهدات',
    subscribe: 'اشترك',
  },
  en: {
    saved: 'Favourites',
    reply: 'The advertiser replied',
    row1: 'Villa · Dammam',
    row2: 'Apartment · Makkah',
    perYear: '/yr',
    broker: 'Licensed broker',
    appraiser: 'Certified appraiser',
    manager: 'Property management',
    riyadh: 'Riyadh',
    jeddah: 'Jeddah',
    dammam: 'Dammam',
    monthly: 'Monthly',
    yearly: 'Yearly',
    plan: 'Professional plan',
    feature1: 'More listings',
    feature2: 'Featured listings',
    feature3: 'View statistics',
    subscribe: 'Subscribe',
  },
} as const;
type Mock = (typeof MOCK)[keyof typeof MOCK];

export default function HomeNextSteps() {
  const { t, i18n } = useTranslation(['home']);
  const isRTL = i18n.dir() === 'rtl';
  const m: Mock = isRTL ? MOCK.ar : MOCK.en;
  const { isAuthed } = usePromoContext();

  const card = (key: string, to: string, visual: JourneyCard['visual']): JourneyCard => ({
    key,
    to,
    title: t(`home.v2.nextSteps.${key}.title`),
    body: t(`home.v2.nextSteps.${key}.body`),
    cta: t(`home.v2.nextSteps.${key}.cta`),
    visual,
  });

  const cards = [
    isAuthed ? card('favorites', '/favorites', <SavedMock m={m} />) : card('account', '/register', <SavedMock m={m} />),
    card('professionals', '/professionals', <ProsMock m={m} />),
    card('plans', '/membership', <PlansMock m={m} />),
  ];

  return (
    <section className="bg-white pb-16 sm:pb-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading title={t('home.v2.nextSteps.title')} isRTL={isRTL} />
        <JourneyCards cards={cards} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Mockups. Decorative (aria-hidden at the stage); rows rise in once on scroll.
// ---------------------------------------------------------------------------

function SavedHeart() {
  // The saved heart is red (THEME.md §2.4c).
  return <i className="ri-heart-fill shrink-0 text-[14px] leading-none text-[#C0322A]" />;
}

/** The account: saved homes, and a reply from an advertiser. */
function SavedMock({ m }: { m: Mock }) {
  return (
    <>
      <PhoneTop>
        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--pv-color-text-muted)]">
          <i className="ri-heart-line text-[12px] leading-none" />
          {m.saved}
        </p>
      </PhoneTop>
      {/* A notification above the phone, as it would slide in. */}
      <Reveal delay={0.3} className="absolute end-4 top-1">
        <div className="flex items-center gap-2 rounded-full bg-[var(--pv-color-surface-strong)] py-1.5 pe-3 ps-1.5 text-start shadow-[0_10px_24px_-12px_rgba(16,24,40,0.35)] ring-1 ring-[var(--pv-border-subtle)]">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#004D40] text-[11px] text-white dark:bg-[#1B7566]">
            <i className="ri-chat-3-line" />
          </span>
          <span className="whitespace-nowrap text-[11px] font-medium text-[var(--pv-color-text)]">{m.reply}</span>
        </div>
      </Reveal>
      <div className="absolute bottom-6 left-1/2 w-[244px] -translate-x-1/2 space-y-2">
        <ListingRow img={dammamImg} title={m.row1} price="1,150,000" chip={<SavedHeart />} delay={0.05} />
        <ListingRow img={makkahImg} title={m.row2} price="60,000" per={m.perYear} chip={<SavedHeart />} delay={0.15} />
      </div>
    </>
  );
}

function ProRow({ role, place, icon, delay }: { role: string; place: string; icon: string; delay: number }) {
  return (
    <Reveal delay={delay}>
      <div className="flex items-center gap-2.5 rounded-xl bg-[var(--pv-color-surface-strong)] p-2.5 text-start shadow-[0_8px_20px_-10px_rgba(16,24,40,0.28)] ring-1 ring-[var(--pv-border-subtle)]">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--pv-color-surface-sunken)] text-[15px] text-[var(--pv-color-primary)]">
          <i className={icon} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-[12px] font-semibold text-[var(--pv-color-text)]">
            <span className="truncate">{role}</span>
            {/* the verified mark: gold, as THEME.md registers it */}
            <i className="ri-verified-badge-fill shrink-0 text-[12px] text-[#B58A0A]" />
          </p>
          <p className="mt-0.5 text-[10.5px] text-[var(--pv-color-text-muted)]">{place}</p>
        </div>
        {/* call, and WhatsApp's green as an outline only */}
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[var(--pv-border-strong)] text-[12px] text-[var(--pv-color-text)]">
          <i className="ri-phone-line" />
        </span>
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[#25D366]/60 text-[12px] text-[#1DA851]">
          <i className="ri-whatsapp-line" />
        </span>
      </div>
    </Reveal>
  );
}

/** The directory: licensed professionals, each a tap away. */
function ProsMock({ m }: { m: Mock }) {
  return (
    <div className="absolute inset-x-5 bottom-4 space-y-2">
      <ProRow role={m.broker} place={m.riyadh} icon="ri-user-3-line" delay={0.05} />
      <ProRow role={m.appraiser} place={m.jeddah} icon="ri-scales-3-line" delay={0.15} />
      <ProRow role={m.manager} place={m.dammam} icon="ri-building-4-line" delay={0.25} />
    </div>
  );
}

/** The plans: a plan's features, monthly or yearly. No price is shown. */
function PlansMock({ m }: { m: Mock }) {
  return (
    <PhoneTop>
      <Reveal delay={0.05}>
        <div className="flex rounded-full bg-[var(--pv-color-surface-sunken)] p-0.5 text-[10.5px] font-semibold">
          <span className="flex-1 rounded-full bg-[#004D40] py-1 text-center text-white dark:bg-[#1B7566]">{m.monthly}</span>
          <span className="flex-1 py-1 text-center text-[var(--pv-color-text-muted)]">{m.yearly}</span>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-2.5 rounded-xl bg-[var(--pv-color-surface-strong)] p-3 shadow-[0_8px_20px_-12px_rgba(16,24,40,0.3)] ring-1 ring-[var(--pv-border-subtle)]">
          <p className="text-[12.5px] font-semibold text-[var(--pv-color-text)]">{m.plan}</p>
          <ul className="mt-2 space-y-1.5">
            {[m.feature1, m.feature2, m.feature3].map((feature) => (
              <li key={feature} className="flex items-center gap-1.5 text-[11px] text-[var(--pv-color-text)]">
                <i className="ri-check-line text-[12px] leading-none text-[var(--pv-color-primary)]" />
                {feature}
              </li>
            ))}
          </ul>
          <span className="mt-3 block rounded-full bg-[#004D40] py-1.5 text-center text-[11px] font-semibold text-white dark:bg-[#1B7566]">
            {m.subscribe}
          </span>
        </div>
      </Reveal>
    </PhoneTop>
  );
}
